/* eslint-disable no-dupe-class-members */
const { USER_SESSION_INFO } = require('../../constants/cookieConstants');
const dayjs = require('../../server/utils/dayjs');
const { createServerTimingHeader } = require('../../server/utils/serverTimingHeader');
const {
  ENTITY_TYPES,
  FILTER_TYPES,
  ROUTE_RESERVED_KEYWORDS,
  ORG_TYPES,
  REGULAR_EXPRESSIONS,
  OPERATORS,
  ENTITY_TYPE_ID_PREFIX,
  ROUTE_PART_TYPE,
  ROUTE_VALIDITY,
  DEFAULT_LANGUAGE,
  FILTER_TYPE_ENTITY_TYPE_MAP,
  RESPONSE_STATUS_CODES,
  SPECIAL_KEYWORD_CONTEXT_PARAMS,
  ROUTE_DATE_FORMATS,
  ENTITY_VALIDATION_STATUS,
  RENDERING_STATUSES,
  VISITOR_TRUST_SOURCES,
} = require('../constants');
const { ROUTE_CONFIGURATIONS } = require('../constants/configurations');
const { transformDatePath, getURLPathAndQuery } = require('../utils');
const { checkEntityAccess } = require('../utils/apis');
const getEntitySlug = require('../utils/getEntitySlug');

const BreadcrumbsResolver = require('./BreadcrumbsResolver');
const FilterParamsTransformer = require('./FilterParamsTransformer');
const H1TitleResolver = require('./H1TitleResolver');
const IdentityResolver = require('./IdentityResolver');
const LinkGenerator = require('./LinkGenerator');
const LinkPropsExtractor = require('./LinkPropsExtractor');
const MatchingEntityFinder = require('./MatchingEntityFinder');
const PageDetailsResolver = require('./PageDetailsResolver');
const RoutePathContext = require('./RoutePathContext');
const ServerTiming = require('./ServerTiming');
const TrustInfoResolver = require('./TrustInfoResolver');

const TIMERS = {
  ENTITY_MATCH: 'entity-match', // NOTE: 1.1
  EXTRACT_LINK_PROPS: '1.2-extract-link-props',
  WITH_PAGE_ENTITY: '1.3-enrich-state-with-page-entity',
  WITH_DEFAULT_FILTERS: '1.4-enrich-state-with-default-filters',
  WITH_ROUTE_VALIDITY: '1.5-enrich-state-with-route-validity',
  WITH_FILTER_PARAMS: '1.7-enrich-state-with-filter-params',
  PAGE_STATS: 'page-stats', // NOTE: 1.8
  WITH_BREADCRUMBS: '1.9-enrich-state-with-breadcrumbs',
  WITH_H1_TITLE: '1.10-enrich-state-with-h1-title',
  WITH_IDENTITY: '1.11-enrich-state-with-identity',
  WITH_TRUST_INFO: '1.12-enrich-state-with-trust-info',
  WITH_STATUS_CODE: '1.13-enrich-state-with-status-code',
};

class RouteContext {
  #language = DEFAULT_LANGUAGE;

  #enabledDebugger = false;

  #originalUrl = '';

  #path = '';

  #query = {};

  #previousEntityMap = {};

  #entityFinder = null;

  #userIdentifier = null;

  #translationFn = value => value;

  #session = null;

  #nextdata = false;

  #pageDetailsResolver = null;

  #getClientPermissions = null;

  #fingerprint = null;

  #cookies = null;

  #trustInfo = null;

  constructor({
    originalUrl = '',
    path = '',
    query = {},
    previousEntityMap = {},
    previousPageInfo = null,
    previousFilterParams = null,
    language,
    entityFinder,
    fetchFunctions,
    session,
    nextdata,
    translationFn,
    getClientPermissions,
    fingerprint,
    cookies,
    trustInfo,
  }) {
    this.#originalUrl = originalUrl;
    this.#path = path;
    const { lng: _lng, '[...action': _action, subpath: _subpath, context: _context, debug, ...restQuery } = query || {};

    if (debug) {
      let debugContexts = [];

      if (Array.isArray(debug)) {
        debugContexts = debug;
      } else if (typeof debug === 'string') {
        debugContexts = debug.split(',');
      }

      this.#enabledDebugger = debugContexts?.includes('paths');
    }

    this.#query = restQuery;
    this.#previousEntityMap = previousEntityMap;

    if (language) {
      this.#language = language;
    }

    if (entityFinder) {
      this.#entityFinder = entityFinder;
    }

    if (translationFn) {
      this.#translationFn = translationFn;
    }

    if (session) {
      this.#session = session;
    }

    if (nextdata) {
      this.#nextdata = nextdata;
    }

    if (getClientPermissions) {
      this.#getClientPermissions = getClientPermissions;
    }

    this.#pageDetailsResolver = new PageDetailsResolver({
      fetchFunctions,
      session,
      previousPageInfo,
      previousFilterParams,
    });

    if (fingerprint) {
      this.#fingerprint = fingerprint;
    }

    if (cookies) {
      this.#cookies = cookies;
    }

    if (fetchFunctions?.userIdentifier) {
      this.#userIdentifier = fetchFunctions?.userIdentifier;
    }

    if (trustInfo) {
      this.#trustInfo = trustInfo;
    }
  }

  #getTimers() {
    const timer = new ServerTiming(
      [
        {
          name: TIMERS.EXTRACT_LINK_PROPS,
          description: 'Extract link props',
        },
        {
          name: TIMERS.WITH_ROUTE_VALIDITY,
          description: 'Check route validity',
        },
        {
          name: TIMERS.WITH_PAGE_ENTITY,
          description: 'Get page entity',
        },
        {
          name: TIMERS.WITH_FILTER_PARAMS,
          description: 'Transform filter params',
        },
        {
          name: TIMERS.WITH_DEFAULT_FILTERS,
          description: 'Add default filters',
        },
        {
          name: TIMERS.WITH_STATUS_CODE,
          description: 'Assign status code',
        },
        {
          name: TIMERS.WITH_BREADCRUMBS,
          description: 'Generate breadcrumbs',
        },
        {
          name: TIMERS.WITH_H1_TITLE,
          description: 'Generate H1 title',
        },
        {
          name: TIMERS.WITH_IDENTITY,
          description: 'Resolve identity',
        },
        {
          name: TIMERS.WITH_TRUST_INFO,
          description: 'Resolve trust info',
        },
      ],
      this,
    );

    return timer;
  }

  #getRedirectContext({ redirect = '', url = '', paths = [] }) {
    const lengthMismatch = redirect.split('/').length !== url.split('/').length;
    let caseMismatch = false;
    let orderMismatch = false;
    let slugMismatch = false;

    if (!lengthMismatch) {
      caseMismatch = redirect.toLowerCase() === url.toLowerCase();

      orderMismatch = redirect.split('/').every(part => url.includes(part));

      slugMismatch = paths
        .filter(part => part.type === ROUTE_PART_TYPE.VARIABLE)
        .some(part => part.path !== getEntitySlug(part.context));
    }

    const context = {
      lengthMismatch,
      caseMismatch,
      orderMismatch,
      slugMismatch,
    };

    const unknown = Object.values(context).filter(Boolean).length === 0;

    return {
      ...context,
      unknown,
    };
  }

  #enrichStateWithRouteValidity(state) {
    const { language, url: originalPath, tokenized, paths, linkProps = {}, ...rest } = state || {};

    let valid = paths.every(part => part.validity !== ROUTE_VALIDITY.INVALID);

    let redirect = null;

    if (valid) {
      const linkGenerator = new LinkGenerator({ language });
      const { as: finalPath } = linkGenerator.getLinkProps({
        ...linkProps,
        queryParams: rest.query,
        onlyLinkProps: true,
        skipAutoTab: true,
      });
      const [originalPathname] = getURLPathAndQuery(originalPath);
      const [finalPathname] = getURLPathAndQuery(finalPath);
      const shouldRedirect = originalPathname && originalPathname !== finalPathname;

      if (shouldRedirect) {
        valid = false;
        redirect = finalPath;
      }
    }

    return {
      url: originalPath,
      tokenized,
      language,
      ...(redirect && { redirect, redirectContext: this.#getRedirectContext({ redirect, url: originalPath, paths }) }),
      valid,
      paths,
      linkProps,
      ...rest,
    };
  }

  #enrichStateWithPageEntity(state) {
    const page = this.#pageDetailsResolver.getDetails(state);
    const { tab } = page || {};
    const { filters } = state?.linkProps || {};
    const { [FILTER_TYPES.RENTALS]: _rentalsFilter, ...restFilters } = filters || {};

    return {
      ...state,
      ...(tab === ROUTE_RESERVED_KEYWORDS.rentals && {
        linkProps: {
          ...state?.linkProps,
          filters: restFilters,
          tab,
        },
      }),
      page,
    };
  }

  async #enrichStateWithPageStats(state) {
    const { valid, page } = state || {};

    if (!valid) {
      return [state, []];
    }

    const pageStats = await this.#pageDetailsResolver.getPageStats(state);
    const timing = this.#pageDetailsResolver.getServerTiming({
      processOrder: 1.6,
      processName: TIMERS.PAGE_STATS,
    });

    return [
      {
        ...state,
        page: {
          ...page,
          stats: pageStats,
        },
      },
      timing,
    ];
  }

  #enrichStateWithFilterParams(state) {
    const { valid, linkProps, entityMap, page } = state || {};

    if (!valid) {
      return state;
    }

    const { filters } = linkProps || {};

    const filterParamsTransformer = new FilterParamsTransformer();
    const filterParams = filterParamsTransformer.transformFilters({ filters, entityMap, page });

    return {
      ...state,
      filterParams,
    };
  }

  #enrichStateWithLinkProps(state) {
    const linkPropsExtractor = new LinkPropsExtractor();
    const { paths, basePath, mainPath, subPath, linkProps } = linkPropsExtractor.getProps(state);
    const filterKeys = Object.keys(linkProps?.filters || {}) || [];
    let query = state?.query || {};

    if (filterKeys.length && !linkProps?.edit) {
      query = Object.entries(state?.query || {}).reduce((acc, [key, value]) => {
        if (!filterKeys.includes(key)) {
          acc[key] = value;
        }

        return acc;
      }, {});
    }

    return {
      ...state,
      query,
      paths,
      basePath,
      mainPath,
      subPath,
      linkProps,
    };
  }

  #enrichStateWithDefaultFilters(state) {
    const { page, linkProps } = state || {};
    const { pro, isListingProductions, tab } = page || {};
    const { filters } = linkProps || {};

    const {
      [FILTER_TYPES.DATE]: dateFilters,
      [FILTER_TYPES.WHO]: _whoFilters,
      [FILTER_TYPES.WHERE]: _whereFilters,
      [FILTER_TYPES.WHAT]: _whatFilters,
      [FILTER_TYPES.GENRE]: _genreFilters,
      [FILTER_TYPES.TICKET]: _ticketFilters,
      ...restFilters
    } = filters || {};

    const hasDateFilters = !!dateFilters?.date_from || !!dateFilters?.date_to;

    if (
      hasDateFilters ||
      !isListingProductions ||
      pro ||
      Object.keys(restFilters).length > 0 ||
      tab === ROUTE_RESERVED_KEYWORDS.rentals
    ) {
      return state;
    }

    return {
      ...state,
      linkProps: {
        ...linkProps,
        filters: {
          ...filters,
          [FILTER_TYPES.DATE]: {
            date_from: dayjs().locale('en').format(ROUTE_DATE_FORMATS.STANDARD),
          },
        },
      },
    };
  }

  async #enrichStateWithStatusCode(state) {
    const { valid, redirect, page, trust } = state || {};
    const { entityType, entity } = page || {};
    let permissionCheckTiming = [];

    let status = RESPONSE_STATUS_CODES.SUCCESS;

    if (!valid) {
      status = redirect ? RESPONSE_STATUS_CODES.PERMANENT_REDIRECT : RESPONSE_STATUS_CODES.NOT_FOUND;
    } else if (
      [ENTITY_TYPES.PROFILE, ENTITY_TYPES.ORGANIZATION, ENTITY_TYPES.AGENCY].includes(entityType) &&
      entity?.id &&
      entity?.validation !== ENTITY_VALIDATION_STATUS.APPROVED
    ) {
      const sessionInfo = this.#session?.get(USER_SESSION_INFO);

      if (sessionInfo?.user?.id || this.#getClientPermissions) {
        const startTime = Date.now();
        const access = this.#getClientPermissions
          ? this.#getClientPermissions()
          : await checkEntityAccess(
              {
                userId: sessionInfo?.user?.id,
                entityType,
                entityId: entity?.id,
              },
              this.#session,
            );

        const { data: permissions, serverTiming } = access || {};
        permissionCheckTiming = (serverTiming || []).map((timer, index) => ({
          ...timer,
          name: TIMERS.WITH_STATUS_CODE.replace(/([\d.]+)-(.+)/, `$1.${index + 1}-$2`),
          description: `User Permissions API: ${timer.description}`,
        }));

        const hasEditPermission = (permissions || []).some(permission => {
          if (entityType === ENTITY_TYPES.AGENCY) {
            return (
              permission?.identifier?.includes(`agency.management:allow:meta:42`) ||
              permission?.identifier?.includes(`agency.management:allow:${entityType}:${entity?.id}`)
            );
          }

          return (
            permission?.identifier?.includes(`editPage:allow:meta:42`) ||
            permission?.identifier?.includes(`editPage:allow:${entityType}:${entity?.id}`)
          );
        });

        if (!hasEditPermission) {
          status = RESPONSE_STATUS_CODES.NOT_FOUND;
        }

        const duration = Date.now() - startTime;
        permissionCheckTiming.push({
          duration,
          name: TIMERS.WITH_STATUS_CODE.replace(/([\d.]+)-(.+)/, `$1.${permissionCheckTiming.length + 1}-$2`),
          description: `User Permissions API (F): with network lag`,
        });
      } else {
        status = RESPONSE_STATUS_CODES.NOT_FOUND;
      }
    } else if (
      entityType === ENTITY_TYPES.PRODUCTION &&
      entity?.id &&
      ![
        ENTITY_VALIDATION_STATUS.VALIDATED,
        ENTITY_VALIDATION_STATUS.APPROVED,
        ENTITY_VALIDATION_STATUS.APPROVED_UNVERIFIED,
      ].includes(entity?.validation)
    ) {
      status = RESPONSE_STATUS_CODES.NOT_FOUND;
    }

    if (
      status === RESPONSE_STATUS_CODES.SUCCESS &&
      (trust?.status === RENDERING_STATUSES.BLOCK ||
        (trust?.status === RENDERING_STATUSES.UNKNOWN && trust?.source === VISITOR_TRUST_SOURCES.CLIENT))
    ) {
      status = RESPONSE_STATUS_CODES.FORBIDDEN;
    }

    return [
      {
        status,
        ...state,
      },
      permissionCheckTiming,
    ];
  }

  #enrichStateWithBreadcrumbs(state) {
    const { language, valid } = state || {};

    if (!valid) {
      return state;
    }

    const breadcrumbsResolver = new BreadcrumbsResolver({
      language,
      translationFn: this.#translationFn,
    });
    const breadcrumbs = breadcrumbsResolver.resolve(state);

    return {
      ...state,
      breadcrumbs,
    };
  }

  async #enrichStateWithH1Title(state) {
    const { valid } = state || {};

    if (!valid) {
      return state;
    }

    const h1TitleResolver = new H1TitleResolver({
      translationFn: this.#translationFn,
      entityFinder: this.#entityFinder,
      session: this.#session,
    });
    const h1Title = await h1TitleResolver.getH1TitleParts(state);

    return {
      ...state,
      h1Title,
    };
  }

  async #enrichStateWithIdentity(state) {
    const { valid } = state || {};

    if (!valid) {
      return state;
    }

    const identityResolver = new IdentityResolver({
      session: this.#session,
      fingerprint: this.#fingerprint,
      cookies: this.#cookies,
      userIdentifier: this.#userIdentifier,
    });
    const identity = await identityResolver.resolve();

    return {
      ...state,
      identity,
    };
  }

  async #enrichStateWithTrustInfo(state) {
    const trustInfoResolver = new TrustInfoResolver({
      cookies: this.#cookies,
      trustInfo: this.#trustInfo,
    });
    const trust = await trustInfoResolver.resolve();

    return {
      ...state,
      trust,
    };
  }

  #getURLPartToken({ data, isDuplicatePathPart = false }) {
    const { entityType, filterType } = data?.context || {};
    const isValid = data.validity === ROUTE_VALIDITY.VALID;
    const isVariablePart = data.type === ROUTE_PART_TYPE.VARIABLE;
    let urlPartToken = '';

    if (isDuplicatePathPart) {
      const token = `${ROUTE_PART_TYPE.INVALID}|${ROUTE_PART_TYPE.DUPLICATE}`;

      if (isVariablePart) {
        urlPartToken = `[${token}]`;
      } else {
        urlPartToken = token;
      }

      return urlPartToken;
    }

    if (isVariablePart) {
      let token = '';

      if (isValid) {
        token = entityType;

        if (data?.context?.isVenue) {
          token += `|${ORG_TYPES.VENUE}`;
        }
      } else {
        token = ROUTE_PART_TYPE.INVALID;

        if (data.reason) {
          token += `|${data.reason}`;
        }
      }

      if (isValid && filterType && token !== filterType) {
        token += `|${filterType}`;
      }

      urlPartToken = `[${token}]`;
    } else if (isValid) {
      urlPartToken = data.path;
    } else {
      urlPartToken = ROUTE_PART_TYPE.INVALID;

      if (data.reason) {
        urlPartToken += `|${data.reason}`;
      }
    }

    return urlPartToken;
  }

  #getPathParts() {
    const pathParts = this.#path.split('/') || [];

    return pathParts.filter(Boolean);
  }

  #getIdsFromQuery() {
    const query = this.#query || {};
    const ids = Object.keys(query).reduce((acc, key) => {
      if (
        [
          FILTER_TYPES.PERFORMANCE_HIGHLIGHT,
          FILTER_TYPES.BOOLEAN_SEARCH,
          FILTER_TYPES.COVER_DEBUT,
          FILTER_TYPES.SINCE_YEAR,
        ].includes(key)
      ) {
        return acc;
      }

      let values = [];

      if (typeof query[key] === 'string') {
        values =
          query[key]?.split(
            [
              FILTER_TYPES.CONDUCTOR,
              FILTER_TYPES.COMPOSER,
              FILTER_TYPES.CHOREOGRAPHER,
              FILTER_TYPES.DIRECTOR,
              FILTER_TYPES.CO_PRODUCER,
              FILTER_TYPES.ROLE,
              FILTER_TYPES.PRODUCER,
              FILTER_TYPES.ENSEMBLE,
              FILTER_TYPES.VENUE,
              FILTER_TYPES.ROLE_PROFESSION,
            ].includes(key)
              ? OPERATORS.SEPARATOR
              : REGULAR_EXPRESSIONS.BOOLEAN_ID_SPLITTER,
          ) || [];
      } else if (Array.isArray(query[key])) {
        values = query[key] || [];
      }

      if (values.length) {
        if ([FILTER_TYPES.GENRE, FILTER_TYPES.WORK_TYPE, FILTER_TYPES.COUNTRY].includes(key)) {
          const optionIdsList = values.reduce((idList, value) => {
            const [parentId, childId] = value.split(OPERATORS.CONCAT);

            if (parentId) {
              const parentEntityType = key === FILTER_TYPES.COUNTRY ? ENTITY_TYPES.COUNTRY : ENTITY_TYPES.WORK_TYPE;
              const prefix = ENTITY_TYPE_ID_PREFIX[parentEntityType];
              idList.push(`${prefix}${parentId}`);
            }

            if (childId) {
              const childEntityType = key === FILTER_TYPES.COUNTRY ? ENTITY_TYPES.CITY : ENTITY_TYPES.STAGING_TYPE;
              const prefix = ENTITY_TYPE_ID_PREFIX[childEntityType];
              idList.push(`${prefix}${childId}`);
            }

            return idList;
          }, []);

          acc.push(...optionIdsList);
        } else if (key === FILTER_TYPES.WHAT) {
          const workList = values.map(value => {
            const prefix = ENTITY_TYPE_ID_PREFIX[ENTITY_TYPES.WORK];
            return `${prefix}${value}`;
          });

          acc.push(...workList);
        } else if (
          [FILTER_TYPES.LANGUAGE, FILTER_TYPES.WORK, FILTER_TYPES.PROFESSION, FILTER_TYPES.SURTITLE].includes(key)
        ) {
          const entityType = FILTER_TYPE_ENTITY_TYPE_MAP[key];
          const list = values.map(value => {
            const prefix = ENTITY_TYPE_ID_PREFIX[entityType];
            return `${prefix}${value}`;
          });
          acc.push(...list);
        } else if (
          [
            FILTER_TYPES.CONDUCTOR,
            FILTER_TYPES.COMPOSER,
            FILTER_TYPES.CHOREOGRAPHER,
            FILTER_TYPES.DIRECTOR,
            FILTER_TYPES.CO_PRODUCER,
            FILTER_TYPES.VENUE,
            FILTER_TYPES.COVER_DEBUT,
            FILTER_TYPES.ROLE_PROFESSION,
          ].includes(key)
        ) {
          acc.push(...[...values.flatMap(str => str.split(REGULAR_EXPRESSIONS.BOOLEAN_ID_SPLITTER))]);

          if (![FILTER_TYPES.CO_PRODUCER].includes(key)) {
            acc.push(key);
          }
        } else if ([FILTER_TYPES.ROLE, FILTER_TYPES.PRODUCER, FILTER_TYPES.ENSEMBLE].includes(key)) {
          const roleIds = values?.reduce((accumulator, value) => {
            const [profession, entityIds] = value.split(OPERATORS.TYPE_SEPARATOR);

            if (!profession?.startsWith(OPERATORS.FILTER_TYPE_NEGATED)) {
              accumulator.push(
                ...[
                  ...(entityIds
                    ?.split(REGULAR_EXPRESSIONS.BOOLEAN_ID_SPLITTER)
                    ?.map(s => s.replace(OPERATORS.FILTER_TYPE_NEGATED, '')) || []),
                  profession,
                ],
              );
            }

            return accumulator;
          }, []);
          acc.push(...roleIds);
        } else {
          const filteredValues = values.filter(value => /^[a-z]\d+$/.test(value));

          acc.push(...filteredValues);
        }
      }

      return acc;
    }, []);

    return ids;
  }

  #isDuplicatePathPart(prevPartState, currentPartState) {
    const { path: prevPath, type: prevType, context: prevContext } = prevPartState || {};
    const { entityType: prevEntityType, entity: prevEntity } = prevContext || {};
    const { path: currentPath, type: currentType, context: currentContext } = currentPartState || {};
    const { entityType: currentEntityType, entity: currentEntity } = currentContext || {};

    return (
      prevPath === currentPath &&
      prevType === currentType &&
      prevEntityType === currentEntityType &&
      prevEntity?.id === currentEntity?.id
    );
  }

  async context() {
    const timer = this.#getTimers();

    const pathParts = this.#getPathParts();
    const lastIndex = pathParts.length - 1;
    const isSeasonFilterAllowed = [ROUTE_RESERVED_KEYWORDS.seasons].includes(pathParts[0]);

    const { parts, slugs } = pathParts.reduce(
      (acc, part, index) => {
        const [path, filterTypePath] = part.split(OPERATORS.FILTER_TYPE_OPERATOR).reverse();
        let configuration = ROUTE_CONFIGURATIONS[path] || null;
        /* NOTE: 
          We are checking configuration here to determine type of path
          because some reserved keywords are not allowed in the URL path.
          Example "january" is a reserved keyword, but it usage is as of variable path
          where "january" can only appear as subset of a variable path of url.
        */
        const type = configuration ? ROUTE_PART_TYPE.RESERVED : ROUTE_PART_TYPE.VARIABLE;
        const isVariablePart = type === ROUTE_PART_TYPE.VARIABLE;
        let filterType = filterTypePath || SPECIAL_KEYWORD_CONTEXT_PARAMS[path]?.type || null;

        const hasNoFilterType = !filterType;
        const isLastPart = lastIndex === index;
        const isNextPartReserved = !!ROUTE_CONFIGURATIONS[pathParts[index + 1]];
        const isSeasonPart = index > 0 && isSeasonFilterAllowed && REGULAR_EXPRESSIONS.YEAR_MATCH.test(path);
        const shouldCheckForDatePath =
          hasNoFilterType && isVariablePart && (isLastPart || isNextPartReserved || isSeasonPart);
        let isValidDate = null;

        if (shouldCheckForDatePath) {
          const { valid, start, end, error } = transformDatePath(path);
          isValidDate = valid;

          if (valid) {
            filterType = isSeasonFilterAllowed ? FILTER_TYPES.SEASON : FILTER_TYPES.DATE;
            configuration = {
              entityType: isSeasonFilterAllowed ? ENTITY_TYPES.SEASON : ENTITY_TYPES.DATE,
              start,
              end,
            };
          } else {
            isValidDate = error;
          }
        }

        const shouldCheckForSlug = ![
          FILTER_TYPES.DATE,
          FILTER_TYPES.SEASON,
          FILTER_TYPES.TICKET,
          FILTER_TYPES.WATCH_ONLINE,
          FILTER_TYPES.RENTALS,
        ].includes(filterType);

        if (isVariablePart && shouldCheckForSlug) {
          acc.slugs.push(path);
        }

        const shouldCheckForFilterType = [
          FILTER_TYPES.CONDUCTOR,
          FILTER_TYPES.CHOREOGRAPHER,
          FILTER_TYPES.DIRECTOR,
          FILTER_TYPES.COMPOSER,
        ].includes(filterType);

        if (shouldCheckForFilterType) {
          acc.slugs.push(filterType);
        }

        acc.parts.push({
          path,
          filterType,
          type,
          configuration,
          ...(this.#enabledDebugger && {
            debug: {
              hasNoFilterType,
              isLastPart,
              isNextPartReserved,
              isSeasonPart,
              shouldCheckForDatePath,
              shouldCheckForSlug,
              shouldCheckForFilterType,
              isValidDate,
            },
          }),
        });

        return acc;
      },
      {
        parts: [],
        slugs: [],
      },
    );

    const idsFromQuery = this.#getIdsFromQuery();

    const entityFinder = new MatchingEntityFinder({
      parts,
      previousEntityMap: this.#previousEntityMap,
      entityFinder: this.#entityFinder,
      session: this.#session,
      language: this.#language,
    });

    let entityMap = {};

    const baseState = {
      url: this.#originalUrl,
      query: this.#query,
      language: this.#language,
      tokenized: '',
      paths: [],
      page: {},
      nextdata: this.#nextdata,
      buildNumber: process.env.BUILD_NUMBER,
    };

    try {
      entityMap = await entityFinder.fetchEntities({ slugs: [...slugs, ...idsFromQuery] });
    } catch (err) {
      if (err.code === 'ECONNABORTED') {
        const timing = timer.timing();

        return [
          {
            ...baseState,
            status: RESPONSE_STATUS_CODES.INTERNAL_SERVER_ERROR,
          },
          createServerTimingHeader(timing),
        ];
      }
    }

    const isCastingPath = parts?.[0]?.path === ROUTE_RESERVED_KEYWORDS.casting;

    const state = parts.reduce(
      (acc, part, index) => {
        const parentPathState = acc.paths[acc.paths.length - 1];
        const parentEntityType = parentPathState?.context?.entityType;
        const matchedEntity = entityFinder.getEntityForPartIndex(index);

        const routePath = new RoutePathContext({
          index,
          parts,
          matchedEntity,
          parentEntityType,
          baseEntityType: acc.paths[isCastingPath ? 1 : 0]?.context?.entityType,
          isParentPathValid: parentPathState?.validity === ROUTE_VALIDITY.VALID,
        });

        const data = routePath.context();
        const isDuplicatePathPart = this.#isDuplicatePathPart(parentPathState, data);

        if (!isDuplicatePathPart) {
          acc.paths.push(data);
        }

        acc.tokenized += `/${this.#getURLPartToken({ data, isDuplicatePathPart })}`;

        return acc;
      },
      {
        ...baseState,
        entityMap,
      },
    );

    const withLinkProps = timer.wrap(this.#enrichStateWithLinkProps, TIMERS.EXTRACT_LINK_PROPS)(state);

    const withPageEntity = timer.wrap(this.#enrichStateWithPageEntity, TIMERS.WITH_PAGE_ENTITY)(withLinkProps);

    const withDefaultFilters = timer.wrap(
      this.#enrichStateWithDefaultFilters,
      TIMERS.WITH_DEFAULT_FILTERS,
    )(withPageEntity);

    const withRouteValidity = timer.wrap(
      this.#enrichStateWithRouteValidity,
      TIMERS.WITH_ROUTE_VALIDITY,
    )(withDefaultFilters);

    const withFilterParams = timer.wrap(
      this.#enrichStateWithFilterParams,
      TIMERS.WITH_FILTER_PARAMS,
    )(withRouteValidity);

    const [withPageStats, pageStatsFetchTiming] = await this.#enrichStateWithPageStats(withFilterParams);

    const withBreadcrumbs = timer.wrap(this.#enrichStateWithBreadcrumbs, TIMERS.WITH_BREADCRUMBS)(withPageStats);

    const withH1Title = await timer.asyncWrap(
      this.#enrichStateWithH1Title.bind(this),
      TIMERS.WITH_H1_TITLE,
    )(withBreadcrumbs);

    const withIdentity = await timer.asyncWrap(
      this.#enrichStateWithIdentity.bind(this),
      TIMERS.WITH_IDENTITY,
    )(withH1Title);

    const withTrustInfo = await timer.asyncWrap(
      this.#enrichStateWithTrustInfo.bind(this),
      TIMERS.WITH_TRUST_INFO,
    )(withIdentity);

    const [withStatusCode, permissionCheckTiming] = await timer.asyncWrap(
      this.#enrichStateWithStatusCode.bind(this),
      TIMERS.WITH_STATUS_CODE,
    )(withTrustInfo);

    const timing = timer.timing();

    const matchEntityFinderTiming = entityFinder.getServerTiming({
      processOrder: 1.1,
      processName: TIMERS.ENTITY_MATCH,
    });

    const serverTiming = createServerTimingHeader([
      ...timing,
      ...matchEntityFinderTiming,
      ...pageStatsFetchTiming,
      ...permissionCheckTiming,
    ]);

    return [withStatusCode, serverTiming];
  }
}

module.exports = RouteContext;
